import React, {
  cloneElement,
  forwardRef,
  PropsWithChildren,
  ReactElement,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  StyledCommandPalette,
  StyledCommandPaletteWrapper,
} from "./CommandPalette.styles";
import { CommandPaletteCommandItem } from "./CommandPaletteCommandItem";
import { CommandPaletteHeader } from "./CommandPaletteHeader";
import {
  CommandPaletteItem,
  CommandPaletteItemProps,
} from "./CommandPaletteItem";
import { CommandPaletteList } from "./CommandPaletteList";
import {
  ModeContextProvider,
  SelectedContextProvider,
  SearchContextProvider,
} from "./context";
import { deepMap } from "../../utils/deepChildrenMap";
import { Dialog, DialogProps } from "../Dialog";

export interface SearchContext {
  name: string;
  class: string;
}

export interface CommandPaletteProps extends DialogProps {
  onChangeSearch: (search: string) => void;
  search: string;
  mode: string | SearchContext;
  setMode: (value: string) => void;
  loading?: boolean;
  idModeEnabled?: boolean;
}

export const _CommandPalette = forwardRef<
  HTMLDivElement,
  PropsWithChildren<CommandPaletteProps>
>(
  (
    {
      children,
      onChangeSearch,
      search,
      mode,
      setMode,
      loading,
      idModeEnabled = false,
      ...rest
    },
    ref
  ) => {
    const [selected, setSelected] = useState<number>(0);
    const listWrapperRef = useRef<HTMLDivElement>(null);

    const scrollToTop = () => {
      listWrapperRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    };

    const scrollToBottom = () => {
      listWrapperRef.current?.scrollTo({
        top: listWrapperRef.current.scrollHeight,
        behavior: "smooth",
      });
    };

    const filterChildren = useMemo(() => {
      let itemsLength = 0;
      let commandsLength = 0;

      const items = deepMap(children, (child) => {
        const item = child as ReactElement<CommandPaletteItemProps>;
        let clonedElement;

        if (item && item.type === CommandPaletteList) {
          clonedElement = cloneElement(item, {
            ...item.props,
          });
        }

        if (item && item.type === CommandPaletteCommandItem) {
          clonedElement = cloneElement(item, {
            ...item.props,
            index: itemsLength,
            selected,
          });

          itemsLength++;
          commandsLength++;
        }

        if (item && item.type === CommandPaletteItem) {
          clonedElement = cloneElement(item, {
            ...item.props,
            index: itemsLength,
            selected,
          });

          itemsLength++;
        }

        return clonedElement;
      });

      return { items, itemsLength, commandsLength };
    }, [children, selected]);

    const handleChangeSelected = (direction?: "up" | "down") => {
      const items = document.querySelectorAll(".cp-item");

      let index = 0;
      let newIndex = 0;
      let newItem: Element | null = null;

      if (direction === "down") {
        items.forEach((_, i) => {
          if (i === selected) {
            index = i;
          }
        });

        newIndex = index === items.length - 1 ? 0 : index + 1;
      } else if (direction === "up") {
        items.forEach((_, i) => {
          if (i === selected) {
            index = i;
          }
        });

        newIndex = !index ? items.length - 1 : index - 1;
      } else {
        setSelected(0);
      }

      newItem = items[newIndex];

      if (newItem && typeof newIndex === "number") {
        setSelected(newIndex);

        newItem.scrollIntoView({
          behavior: "smooth",
          block: newIndex ? "center" : "end",
        });
      }
    };

    return (
      <Dialog {...rest} ref={ref} animate="fade">
        <SearchContextProvider value={{ search, onChangeSearch }}>
          <SelectedContextProvider value={{ selected: selected, setSelected }}>
            <StyledCommandPalette
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  handleChangeSelected("down");
                }

                if (e.key === "ArrowUp") {
                  e.preventDefault();
                  handleChangeSelected("up");
                }

                if (e.key === "End") {
                  e.preventDefault();
                  scrollToBottom();
                  setSelected(filterChildren.itemsLength - 1);
                }

                if (e.key === "Home") {
                  e.preventDefault();
                  scrollToTop();
                  setSelected(0);
                }
              }}
            >
              <ModeContextProvider value={{ mode, setMode, idModeEnabled }}>
                <CommandPaletteHeader
                  commandsLength={filterChildren.commandsLength}
                  itemsLength={filterChildren.itemsLength}
                  loading={loading}
                />
              </ModeContextProvider>

              <StyledCommandPaletteWrapper ref={listWrapperRef}>
                {filterChildren.items}
              </StyledCommandPaletteWrapper>
            </StyledCommandPalette>
          </SelectedContextProvider>
        </SearchContextProvider>
      </Dialog>
    );
  }
);

_CommandPalette.displayName = "CommandPalette";

// https://github.com/DefinitelyTyped/DefinitelyTyped/issues/34757
export const CommandPalette = Object.assign({}, _CommandPalette, {
  List: CommandPaletteList,
  Item: CommandPaletteItem,
  CommandItem: CommandPaletteCommandItem,
});
